import { Component } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
    ConfirmationDialogComponent,
    ConfirmationDialogOption,
    ConfirmationDialogType,
} from '@components/confirmation-dialog/confirmation-dialog.component';
import { language } from '@models/settings';
import { EnvironmentService } from '@service/environment.service';
import { LanguageService } from '@service/language.service';
import { SyncDatabaseService } from '@service/sync-database.service';
import { VesselService } from '@service/vessel.service';
import { RuntimeEnvironments } from '@src/app/models/runtime-enviroment.enum';
import { lastValueFrom } from 'rxjs';

@Component({
    selector: 'app-settings',
    templateUrl: './settings.component.html',
    styleUrls: ['./settings.component.css'],
    standalone: false,
})
export class SettingsComponent {
    readonly LANGUAGE_OPTIONS = language.map(lan => lan.label);

    selectedLanguage: any;
    isLoading: boolean = false;
    private env: RuntimeEnvironments;

    constructor(
        private languageService: LanguageService,
        private vesselService: VesselService,
        private environmentService: EnvironmentService,
        private dialog: MatDialog,
        private syncDatabaseService: SyncDatabaseService
    ) {
        this.env = this.environmentService.getEnvironment();
    }

    ngOnInit(): void {
        this.vesselService.setActiveVessel(null);
        const currentLanguage = this.languageService.getLanguage();
        const findLanguage = language.find(lan => lan.value === currentLanguage);
        this.selectedLanguage = findLanguage.label;
    }

    languageSelect(event) {
        const findLanguage = language.find(lan => lan.label === event.value);
        this.languageService.setLocalStorageLanguage(findLanguage.value);
    }

    toggleSize() {
        if (!this.isRunningInElectron) return;
        (window as any).api.toggleFullScreenElectron();
    }

    async closeWindow() {
        if (!this.isRunningInElectron) return;

        const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
            width: '415px',
            data: {
                type: ConfirmationDialogType.SIMPLE,
                title: 'settings.electron.close.confirm.title',
                message: 'settings.electron.close.confirm.sub-title',
                discardButton: 'dlg.cancel',
                saveButton: 'dlg.close',
                width: '452px',
            },
        });

        const confirmationOption = await lastValueFrom(dialogRef.afterClosed());

        if (confirmationOption == ConfirmationDialogOption.SAVE) (window as any).api.closeWindow();
    }

    async reboot() {
        if (!this.isRunningInElectron) return;

        const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
            width: '415px',
            data: {
                type: ConfirmationDialogType.SIMPLE,
                title: 'settings.electron.reboot.confirm.title',
                message: 'settings.electron.close.confirm.sub-title',
                discardButton: 'dlg.cancel',
                saveButton: 'settings.electron.reboot',
                width: '452px',
            },
        });

        const confirmationOption = await lastValueFrom(dialogRef.afterClosed());

        if (confirmationOption == ConfirmationDialogOption.SAVE) (window as any).api.restartApp();
    }

    async syncDatabase() {
        if (!this.isRunningInElectron) return;

        const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
            width: '415px',
            data: {
                type: ConfirmationDialogType.SIMPLE,
                title: 'settings.electron.sync.confirm.title',
                title2: 'settings.electron.sync.confirm.title-2',
                message: 'settings.electron.sync.confirm.message-1',
                message2: 'settings.electron.sync.confirm.message-1',
                discardButton: 'dlg.cancel',
                saveButton: 'settings.electron.sync.confirm.confirm',
                width: '452px',
            },
        });

        const confirmationOption = await lastValueFrom(dialogRef.afterClosed());

        if (confirmationOption == ConfirmationDialogOption.SAVE) {
            try {
                this.isLoading = true;
                await this.syncDatabaseService.syncDatabase();
                (window as any).api.restartApp();
            } catch (err) {
                console.error(err);
            } finally {
                this.isLoading = false;
            }
        }
    }

    get isRunningInElectron() {
        return this.env == RuntimeEnvironments.ELECTRON;
    }
}
